// Thin fetch wrapper — JSON in/out, throws ApiError on non-2xx.

export class ApiError extends Error {
  status: number;
  body: { error?: string } & Record<string, unknown>;

  constructor(status: number, body: { error?: string } & Record<string, unknown>) {
    super(body.error ?? `Request failed (${status})`);
    this.status = status;
    this.body = body;
  }
}

export async function api<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const { json, ...rest } = init ?? {};
  const res = await fetch(path, {
    ...rest,
    headers: {
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...rest.headers,
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  const body = await res.json().catch(() => ({}));
  // Session expiry drops the app to the login screen. Keyed on the server's
  // explicit `code`, not on the 401 alone: /api/auth/* also answers 401 for
  // in-flow credential checks (wrong current password, wrong TOTP code), and
  // treating those as expiry would sign the operator out on a typo.
  if (
    res.status === 401 &&
    (body as { code?: string }).code === 'session_expired' &&
    typeof window !== 'undefined'
  ) {
    window.dispatchEvent(new Event('ss:unauthorized'));
  }
  if (!res.ok) throw new ApiError(res.status, body as { error?: string });
  // Live sidebar counts (Phase 1.11): a successful mutation (complete, trash,
  // create…) fires an app-wide event so the Shell refreshes its actionable
  // badges immediately, without waiting for the next route change.
  const method = (rest.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('ss:mutated'));
    if (/^\/api\/admin\/views(?:[/?]|$)/.test(path)) {
      window.dispatchEvent(new Event('ss:views-changed'));
    }
  }
  return body as T;
}
